import React from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';

import LoadingSpinner from 'src/components/LoadingSpinner';
import PageContainer from 'src/components/PageContainer';
import PageTitle from 'src/components/PageTitle';
import {useCurrentOrgIdPrefix} from 'src/hooks/useCurrentOrgIdPrefix';
import {
  SetupProjectQueryKey,
  SetupSectionProps,
  SetupWizardStateView,
  makeSetupProjectsQueryKey,
  setupWizardApi,
} from 'src/lib/api/setupWizard';

import DefineSitesSection from './DefineSitesSection';
import ObservationsSection from './ObservationsSection';
import ProjectSettingsSection from './ProjectSettingsSection';
import ReviewApplySection from './ReviewApplySection';
import RunDelineationSection from './RunDelineationSection';
import SaveExportSection from './SaveExportSection';
import SetupStepSection from './SetupStepSection';
import {AllStepStatuses, useStepStatus} from './useStepStatus';

type StepId =
  | 'projectSettings'
  | 'defineSites'
  | 'runDelineation'
  | 'reviewApply'
  | 'observations'
  | 'saveExport';

const STEPS: {
  id: StepId;
  number: number;
  title: string;
  Component: React.ComponentType<SetupSectionProps>;
}[] = [
  {id: 'projectSettings', number: 1, title: 'Project Settings', Component: ProjectSettingsSection},
  {id: 'defineSites', number: 2, title: 'Define Sites', Component: DefineSitesSection},
  {id: 'runDelineation', number: 3, title: 'Run Delineation', Component: RunDelineationSection},
  {id: 'reviewApply', number: 4, title: 'Review & Finalize Sites', Component: ReviewApplySection},
  {id: 'observations', number: 5, title: 'Observations', Component: ObservationsSection},
  {id: 'saveExport', number: 6, title: 'Save & Export', Component: SaveExportSection},
];

/** Find the first incomplete step to auto-expand on load. */
function findFirstIncompleteStep(statuses: AllStepStatuses): StepId {
  for (const step of STEPS) {
    const status = statuses[step.id];
    if (status.status !== 'complete') return step.id;
  }
  return 'saveExport'; // all complete, default to last
}

export default function SetupWizardPage() {
  const currentOrgIdPrefix = useCurrentOrgIdPrefix();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {orgId, projectId} = useParams() as {orgId: string; projectId: string};
  const queryKey: SetupProjectQueryKey = [
    'setupWizard',
    'project',
    orgId!,
    projectId!,
  ] as SetupProjectQueryKey;

  const {
    data: payload,
    isLoading,
    error,
  } = useQuery(queryKey, () => setupWizardApi.getProject(orgId!, projectId!), {
    enabled: !!orgId && !!projectId,
  });

  const stepStatuses = useStepStatus(payload);

  const [expandedSteps, setExpandedSteps] = React.useState<Set<StepId> | null>(null);
  const [deleteConfirm, setDeleteConfirm] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  // Auto-expand the first incomplete step once data loads
  React.useEffect(() => {
    if (payload && expandedSteps === null) {
      const firstIncomplete = findFirstIncompleteStep(stepStatuses);
      setExpandedSteps(new Set([firstIncomplete]));
    }
  }, [payload, expandedSteps, stepStatuses]);

  const toggleStep = (stepId: StepId) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  if (!orgId || !projectId) {
    return (
      <PageContainer>
        <PageTitle pageStack={[{title: 'Setup wizard', to: `/${currentOrgIdPrefix}/setup`}]} />
        <p className="text-gray-500">Missing org or project.</p>
      </PageContainer>
    );
  }

  if (isLoading || !payload) {
    return (
      <PageContainer>
        <PageTitle pageStack={[{title: 'Setup wizard', to: `/${currentOrgIdPrefix}/setup`}]} />
        <LoadingSpinner />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageTitle pageStack={[{title: 'Setup wizard', to: `/${currentOrgIdPrefix}/setup`}]} />
        <p className="text-red-600">Failed to load project.</p>
      </PageContainer>
    );
  }

  const state: SetupWizardStateView = payload.data;
  const projectReadable = state.projectReadable ?? state.project ?? projectId;

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await setupWizardApi.deleteProject(orgId, projectId, deleteConfirm);
      // The select page's project list has a 30s staleTime.
      queryClient.invalidateQueries(makeSetupProjectsQueryKey(orgId));
      navigate(`/${currentOrgIdPrefix}/setup`);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Delete failed');
      setIsDeleting(false);
    }
  };

  const expanded = expandedSteps ?? new Set<StepId>();

  const sectionProps = {
    orgId,
    projectId,
    payload,
    queryKey,
  };

  return (
    <PageContainer>
      <PageTitle
        pageStack={[
          {title: 'Setup wizard', to: `/${currentOrgIdPrefix}/setup`},
          {title: state.organizationName ?? orgId, to: `/${currentOrgIdPrefix}/setup`},
          {title: projectReadable, to: `/${currentOrgIdPrefix}/setup/${orgId}/${projectId}`},
        ]}
      />

      {/* Vertical accordion of steps */}
      <div className="space-y-2">
        {STEPS.map((step) => {
          const status = stepStatuses[step.id];
          const {Component} = step;
          return (
            <SetupStepSection
              key={step.id}
              stepNumber={step.number}
              title={step.title}
              status={status.status}
              summary={status.summary}
              errorCount={status.errors.length || undefined}
              isExpanded={expanded.has(step.id)}
              onToggle={() => toggleStep(step.id)}
            >
              <Component
                {...sectionProps}
                stepErrors={status.errors}
                prerequisiteWarning={status.prerequisiteWarning}
              />
            </SetupStepSection>
          );
        })}
      </div>

      {/* Delete project */}
      <div className="mt-12 border-t border-gray-200 pt-6">
        <h2 className="text-sm font-medium text-red-700 mb-2">Delete project</h2>
        <p className="text-sm text-gray-600 mb-3">
          Type the project name <span className="font-mono font-medium">{projectId}</span> to
          confirm deletion.
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="text"
            className="rounded-md border-gray-300 shadow-sm text-sm"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={projectId}
          />
          <button
            type="button"
            disabled={
              isDeleting || deleteConfirm.replace(/ /g, '_') !== projectId.replace(/ /g, '_')
            }
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
            onClick={handleDelete}
          >
            {isDeleting ? 'Deleting…' : 'Delete project'}
          </button>
          {deleteError && <p className="text-red-600 text-sm">{deleteError}</p>}
        </div>
      </div>
    </PageContainer>
  );
}
